"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomAppDatabaseRequirementSharingPolicySchema = void 0;
exports.toCustomAppDatabaseRequirement = toCustomAppDatabaseRequirement;
exports.toCustomAppDatabaseRequirementTarget = toCustomAppDatabaseRequirementTarget;
exports.toCustomAppDatabaseRequirements = toCustomAppDatabaseRequirements;
exports.resolveCustomAppDatabaseRequirementMappings = resolveCustomAppDatabaseRequirementMappings;
const zod_1 = require("zod");
const custom_app_config_1 = require("./custom-app-config");
const custom_app_contract_1 = require("./custom-app-contract");
exports.CustomAppDatabaseRequirementSharingPolicySchema = zod_1.z.enum([
    'shared',
    'dedicated',
]);
function toRequirementShape(input) {
    const sourceKey = 'sourceKey' in input ? input.sourceKey : input.key;
    if (!sourceKey.trim()) {
        throw new Error('Custom App database requirement sourceKey is required');
    }
    const role = custom_app_config_1.CustomAppDataSourceRoleSchema.parse(input.role);
    const contentModel = custom_app_config_1.CustomAppContentModelSchema.parse(input.contentModel);
    const semanticKey = (0, custom_app_contract_1.createCustomAppDatabaseSemanticKey)({
        sourceKey,
        semanticKey: input.semanticKey,
    });
    const schemaFingerprint = input.schemaFingerprint;
    if (!schemaFingerprint?.trim()) {
        throw new Error(`Custom App database requirement ${sourceKey} requires schemaFingerprint`);
    }
    const displayName = input.displayName?.trim() || sourceKey;
    const sharingPolicy = exports.CustomAppDatabaseRequirementSharingPolicySchema.parse(input.sharingPolicy ?? 'shared');
    return {
        requirementKey: input.requirementKey ?? sourceKey,
        sourceKey,
        semanticKey,
        role,
        contentModel,
        schemaFingerprint,
        displayName,
        sharingPolicy,
    };
}
function toCustomAppDatabaseRequirement(input) {
    return toRequirementShape(input);
}
function toCustomAppDatabaseRequirementTarget(input) {
    return toRequirementShape(input);
}
function toCustomAppDatabaseRequirements(inputs) {
    return inputs.map(toCustomAppDatabaseRequirement);
}
function resolveCustomAppDatabaseRequirementMappings(input) {
    const existingByKey = new Map(input.existingTargets.map(target => [target.sourceKey, target]));
    const sortedExistingTargets = [...input.existingTargets].sort((a, b) => a.sourceKey.localeCompare(b.sourceKey));
    const plannedSemanticTargets = new Map();
    const mappings = [];
    const usedTargetKeys = new Set([
        ...input.usedTargetKeys,
        ...existingByKey.keys(),
    ]);
    const allocateTargetKey = (sourceKey) => {
        const base = input.forceProvisionKeyMapping || usedTargetKeys.has(sourceKey)
            ? input.mapInsertedKey(sourceKey)
            : sourceKey;
        let target = base;
        let suffix = 2;
        while (usedTargetKeys.has(target))
            target = `${base}-${suffix++}`;
        usedTargetKeys.add(target);
        return target;
    };
    for (const requirement of input.requirements) {
        const decision = input.decisions?.[requirement.sourceKey];
        if (decision?.action === 'use_existing') {
            const plannedTarget = plannedSemanticTargets.get(requirement.semanticKey);
            const requestedTarget = existingByKey.get(decision.target) ??
                (plannedTarget?.targetKey === decision.target
                    ? plannedRequirementToTarget(plannedTarget)
                    : undefined);
            if (requirement.sharingPolicy !== 'shared' ||
                !requestedTarget ||
                !isSameSemanticDatabase(requirement, requestedTarget) ||
                !isSchemaCompatible(requirement, requestedTarget)) {
                mappings.push(createConflictMapping({
                    requirement,
                    targetKey: decision.target,
                    target: requestedTarget,
                    reason: requirement.sharingPolicy !== 'shared'
                        ? 'sharing_policy'
                        : requestedTarget
                            ? 'incompatible_schema'
                            : 'invalid_reuse_target',
                }));
                continue;
            }
            mappings.push({
                source: requirement.sourceKey,
                target: requestedTarget.sourceKey,
                action: 'use_existing',
            });
            continue;
        }
        if (decision?.action === 'create_new') {
            const plannedSemanticTarget = requirement.sharingPolicy === 'shared'
                ? plannedSemanticTargets.get(requirement.semanticKey)
                : undefined;
            if (plannedSemanticTarget) {
                if (!isSchemaCompatible(requirement, plannedSemanticTarget.requirement)) {
                    mappings.push(createConflictMapping({
                        requirement,
                        targetKey: plannedSemanticTarget.targetKey,
                        target: plannedRequirementToTarget(plannedSemanticTarget),
                        reason: 'incompatible_schema',
                    }));
                    continue;
                }
                mappings.push({
                    source: requirement.sourceKey,
                    target: plannedSemanticTarget.targetKey,
                    action: 'use_existing',
                });
                continue;
            }
            const target = allocateTargetKey(requirement.sourceKey);
            if (requirement.sharingPolicy === 'shared') {
                plannedSemanticTargets.set(requirement.semanticKey, {
                    targetKey: target,
                    requirement,
                });
            }
            mappings.push({
                source: requirement.sourceKey,
                target,
                action: target === requirement.sourceKey ? 'insert' : 'rename',
            });
            continue;
        }
        if (requirement.sharingPolicy === 'shared') {
            // A same-named but incompatible database is not a user-facing conflict.
            // Reuse only a compatible shared target; otherwise allocate a separate
            // database without modifying existing schemas or rows. Explicit reuse
            // decisions above and inconsistent requirements within a block still fail.
            const existingExact = existingByKey.get(requirement.sourceKey);
            const isCompatibleTarget = (target) => isSameSemanticDatabase(requirement, target) &&
                isSchemaCompatible(requirement, target);
            const compatibleTarget = existingExact && isCompatibleTarget(existingExact)
                ? existingExact
                : sortedExistingTargets.find(isCompatibleTarget);
            if (compatibleTarget) {
                mappings.push({
                    source: requirement.sourceKey,
                    target: compatibleTarget.sourceKey,
                    action: 'use_existing',
                });
                continue;
            }
            const semanticKey = requirement.semanticKey;
            const plannedSemanticTarget = plannedSemanticTargets.get(semanticKey);
            if (plannedSemanticTarget) {
                if (!isSchemaCompatible(requirement, plannedSemanticTarget.requirement)) {
                    mappings.push(createConflictMapping({
                        requirement,
                        targetKey: plannedSemanticTarget.targetKey,
                        target: plannedRequirementToTarget(plannedSemanticTarget),
                        reason: 'incompatible_schema',
                    }));
                    continue;
                }
                mappings.push({
                    source: requirement.sourceKey,
                    target: plannedSemanticTarget.targetKey,
                    action: 'use_existing',
                });
                continue;
            }
        }
        const target = allocateTargetKey(requirement.sourceKey);
        if (requirement.sharingPolicy === 'shared') {
            plannedSemanticTargets.set(requirement.semanticKey, {
                targetKey: target,
                requirement,
            });
        }
        mappings.push({
            source: requirement.sourceKey,
            target,
            action: target === requirement.sourceKey ? 'insert' : 'rename',
        });
    }
    return mappings;
}
function plannedRequirementToTarget(input) {
    return {
        ...input.requirement,
        sourceKey: input.targetKey,
    };
}
function isSameSemanticDatabase(a, b) {
    return a.semanticKey === b.semanticKey;
}
function isSchemaCompatible(a, b) {
    if (a.schemaFingerprint === b.schemaFingerprint)
        return true;
    const aSchema = parseCustomAppDatabaseSchemaFingerprint(a.schemaFingerprint);
    const bSchema = parseCustomAppDatabaseSchemaFingerprint(b.schemaFingerprint);
    if (!aSchema || !bSchema)
        return false;
    if (aSchema.role !== bSchema.role ||
        aSchema.contentModel !== bSchema.contentModel) {
        return false;
    }
    if (aSchema.columns.length === 0 || bSchema.columns.length === 0) {
        return true;
    }
    return (stableJsonStringify(aSchema.columns) ===
        stableJsonStringify(bSchema.columns));
}
function parseCustomAppDatabaseSchemaFingerprint(value) {
    const prefix = 'custom-app-db-schema:';
    if (!value.startsWith(prefix))
        return null;
    let parsed;
    try {
        parsed = JSON.parse(value.slice(prefix.length));
    }
    catch {
        return null;
    }
    if (!parsed || typeof parsed !== 'object')
        return null;
    const record = parsed;
    const role = typeof record.role === 'string' ? record.role : null;
    const contentModel = typeof record.contentModel === 'string' ? record.contentModel : null;
    const rawColumns = Array.isArray(record.columns) ? record.columns : null;
    if (!role || !contentModel || !rawColumns)
        return null;
    const columns = rawColumns
        .map(column => normalizeSchemaFingerprintColumn(column))
        .filter((column) => Boolean(column));
    if (columns.length !== rawColumns.length)
        return null;
    return {
        role,
        contentModel,
        columns: columns.sort((a, b) => stableJsonStringify(a).localeCompare(stableJsonStringify(b))),
    };
}
function normalizeSchemaFingerprintColumn(value) {
    if (!value || typeof value !== 'object')
        return null;
    const record = value;
    return {
        name: typeof record.name === 'string' ? record.name : null,
        type: typeof record.type === 'string' ? record.type : null,
        data: stableJsonValue(stripPhysicalSchemaData(record.data ?? null)),
    };
}
function stripPhysicalSchemaData(value) {
    if (Array.isArray(value))
        return value.map(stripPhysicalSchemaData);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value)
            .filter(([key]) => key !== 'targetDatabaseId' && key !== 'inverseFieldId')
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, nested]) => [key, stripPhysicalSchemaData(nested)]));
    }
    return value;
}
function stableJsonValue(value) {
    if (Array.isArray(value))
        return value.map(stableJsonValue);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, nested]) => [key, stableJsonValue(nested)]));
    }
    return value;
}
function stableJsonStringify(value) {
    return JSON.stringify(stableJsonValue(value));
}
function createConflictMapping(input) {
    return {
        source: input.requirement.sourceKey,
        target: input.targetKey,
        action: 'conflict',
        conflictReason: input.reason,
        sourceRequirementKey: input.requirement.requirementKey,
        targetRequirementKey: input.target?.requirementKey ?? input.targetKey,
        sourceSemanticKey: input.requirement.semanticKey,
        targetSemanticKey: input.target?.semanticKey,
        sourceSchemaFingerprint: input.requirement.schemaFingerprint,
        targetSchemaFingerprint: input.target?.schemaFingerprint,
    };
}
